import React, { useCallback } from 'react';
import { useMethods } from 'react-use';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import Modal from '@/components/Modals/Modal';
import DateSelector from '@/modules/events/views/Events/components/DateSelector';
import { inlineDatePickerStyles } from '@/modules/events/views/Events/components/DateSelector/style';
import { EventCalendarStates } from '@/modules/events/views/Events/hooks/useEventsAction';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';

interface KanbanDueDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  draggingItemRef:
    | React.MutableRefObject<string | null>
    | React.MutableRefObject<ActionItemFragment | null>;
  onChangeDueDate: (dueDate: string, id: string) => void;
  onSuccessfulMove?: (id: string) => void;
}

function createMethods(state: EventCalendarStates) {
  return {
    setSelectedCalendarDate(selectedDate: Date) {
      return {
        ...state,
        selectedCalendarDate: selectedDate,
        isUserSelectedDate: true,
      };
    },

    setStartingCalendarMonth(startingMonth: Date) {
      return { ...state, startingCalendarMonth: startingMonth };
    },
  };
}

const getInitialState = (): EventCalendarStates => {
  return {
    isUserSelectedDate: false,
    selectedCalendarDate: new Date(Date.now()),
    startingCalendarMonth: new Date(Date.now()),
  };
};

const KanbanDueDateModal = ({
  isOpen,
  onClose,
  draggingItemRef,
  onChangeDueDate,
  onSuccessfulMove,
}: KanbanDueDateModalProps) => {
  const { t } = useTranslation();
  const [calendarState, calendarMethods] = useMethods(createMethods, getInitialState());
  const { setSelectedCalendarDate, setStartingCalendarMonth } = calendarMethods;
  const { selectedCalendarDate } = calendarState;
  const onSubmit = useCallback(() => {
    if (draggingItemRef.current) {
      const id =
        typeof draggingItemRef.current !== 'string' && 'id' in draggingItemRef.current
          ? draggingItemRef.current.id
          : draggingItemRef.current;
      onChangeDueDate(selectedCalendarDate.toISOString(), id);
      onClose();
      onSuccessfulMove?.(id);
    }
  }, [onChangeDueDate, draggingItemRef, selectedCalendarDate, onClose, onSuccessfulMove]);
  return (
    <Modal
      action={{
        text: t('Common:Actions.Save'),
        onSubmit,
      }}
      header={{ title: 'Select Due Date', description: 'Select updated due date for the task' }}
      open={isOpen}
      onClose={onClose}>
      <Stack className={(cn(inlineDatePickerStyles), 'mx-auto')}>
        <DateSelector
          calendarDaysWithActivity={new Set()}
          selectedCalendarDate={selectedCalendarDate}
          setSelectedCalendarDate={setSelectedCalendarDate}
          setStartingCalendarMonth={setStartingCalendarMonth}
        />
      </Stack>
    </Modal>
  );
};

export default React.memo(KanbanDueDateModal);
